import { Cake, Gift, MessageSquareText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
  useBirthdaysThisWeek,
  buildBirthdayWaLink,
  birthdayDayLabel,
} from "@/hooks/useBirthdaysThisWeek";

/**
 * Dashboard widget — persistent card listing birthdays in the next 7 days.
 * Auto-hides when the role isn't eligible or there are zero upcoming.
 */
export default function BirthdayWidget() {
  const { user } = useAuth();
  const { rows, count, eligible, loading } = useBirthdaysThisWeek(user);

  if (!eligible || loading || count === 0) return null;

  return (
    <div className="bg-white border border-red-200 rounded-lg overflow-hidden shadow-sm" data-testid="birthday-widget">
      <div className="bg-gradient-to-r from-red-500/10 via-red-500/5 to-transparent border-b border-red-100 px-4 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Cake className="h-4 w-4 text-red-700" />
          <h3 className="text-sm font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Birthdays this week
          </h3>
          <span className="bg-red-100 text-red-700 text-[10px] font-bold rounded-full px-2 py-0.5">
            {count}
          </span>
        </div>
        <div className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1">
          <Gift className="h-3 w-3" /> Next 7 days
        </div>
      </div>
      <div className="divide-y divide-slate-100">
        {rows.map(emp => {
          const waLink = buildBirthdayWaLink(emp);
          const isToday = emp.days_until === 0;
          return (
            <div key={emp.id} className={`px-4 py-2.5 flex items-center gap-3 ${isToday ? "bg-red-50/40" : "hover:bg-slate-50"}`} data-testid={`birthday-widget-row-${emp.id}`}>
              <div className={`h-9 w-9 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold ${isToday ? "bg-red-600 text-white" : "bg-red-100 text-red-700"}`}>
                {emp.name?.[0]?.toUpperCase() || "?"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900 flex items-center gap-1.5">
                  {emp.name}
                  {isToday && <span className="text-[9px] bg-red-600 text-white px-1 py-0.5 rounded font-bold">TODAY 🎂</span>}
                  <span className="text-[10px] text-slate-400 font-normal">· {emp.punch_id}</span>
                </div>
                <div className="text-[11px] text-slate-500 flex items-center gap-2">
                  <span className={isToday ? "text-red-700 font-bold" : "text-slate-600"}>
                    {birthdayDayLabel(emp.days_until)}
                  </span>
                  <span>·</span>
                  <span>Turns <b>{emp.age_turning}</b></span>
                  {emp.department_name && <><span>·</span><span>{emp.department_name}</span></>}
                </div>
              </div>
              {waLink ? (
                <a
                  href={waLink}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-emerald-600 hover:bg-emerald-700 text-white rounded transition-colors flex-shrink-0"
                  data-testid={`widget-wish-${emp.id}`}
                >
                  <MessageSquareText className="h-3.5 w-3.5" />
                  Wish
                </a>
              ) : (
                <span className="text-[10px] text-amber-600 italic" title="No phone number on file">no phone</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
